import { type FC, useEffect } from "react";

import { EventContent } from "~/feature/event/components/EventContent";
import { EventMarketsSavedFiltersSync } from "~/feature/event/components/EventMarketsSavedFiltersSync";
import { EventMarketsFiltersProvider } from "~/feature/event/providers/EventMarketsFiltersProvider";
import { EventMarketsSettingsProvider } from "~/feature/event/providers/EventMarketsSettingsProvider";
import { MarketCalculatorProvider } from "~/feature/event/providers/MarketCalculatorProvider";
import type { ICategory } from "~/types/category";
import type {
  IAggregatedEvent,
  IMarket,
  IOutcomeConsensus,
} from "~/types/event";
import { runAfterLayout } from "~/utils/runAfterLayout";
import { scrollWindowToElement } from "~/utils/scrollWindowToElement";

const HASH_SECTION_SCROLL_OFFSET_PX = 10;

interface IEventPageProps {
  event: IAggregatedEvent | null;
  category: ICategory | null;
  markets: IMarket[];
  outcomes: IOutcomeConsensus[];
  initialMarket: IMarket | null;
}

export const EventPage: FC<IEventPageProps> = ({
  event,
  category,
  markets,
  outcomes,
  initialMarket,
}) => {
  const isGroupedEvents = (event?.eventCount || 0) > 1;

  useEffect(() => {
    const hash = window.location.hash;
    const section = hash ? document.querySelector(hash) : null;

    if (!section) {
      return;
    }

    return runAfterLayout(() =>
      scrollWindowToElement(section, HASH_SECTION_SCROLL_OFFSET_PX)
    );
  }, []);

  return (
    <EventMarketsSettingsProvider>
      <EventMarketsFiltersProvider>
        <EventMarketsSavedFiltersSync />
        <MarketCalculatorProvider initialMarket={initialMarket}>
          <EventContent
            event={event}
            isGroupedEvents={isGroupedEvents}
            category={category}
            markets={markets}
            outcomes={outcomes}
          />
        </MarketCalculatorProvider>
      </EventMarketsFiltersProvider>
    </EventMarketsSettingsProvider>
  );
};
